import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ordersService } from '../../../services/orders.service';
import { MessageService } from 'primeng/api';
import { EmsServicesService } from 'src/app/module/ftl-master/services/ems-services.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, switchMap, tap } from 'rxjs';
import { TabService } from 'src/app/shared/tab.service';

export type Role = 'ROLE_KAM' | 'ROLE_PROCUREMENT' | 'ROLE_PROCUREMENT_HEAD' | 'ROLE_VENDOR' | 'ROLE_SUPERUSER';

export type CustomerRateType = 'RATE_KNOWN' | 'NULL_PRICE';

export interface OrderViewModel {
  id: number;
  orderNo: string;
  orderNumber: string;
  lrNumber: string | null;
  customer: string;
  customerName: string;
  accountId: number;
  customerId: number;
  vendorId: number;
  orderType: string;
  vehicleType: string;
  vehiclePd: string;
  vehicleRequiredAt: string;
  tripStatus: string;
  route: string;
  source: string;
  destination: string;
  assignedKam: string;
  assignedKamId: number;
  assignedProcExec: string;
  assignedProcurementId: number;
  numberOfVehicle: number;
  vehicleTypeId: number;
  status: string;
  biddingStatus: string;
  biddingEnable: number;
  customerRateType?: CustomerRateType;
  isNullPrice?: boolean;
  vendorName?: string;
  profitAdjustedCustomerAmount: any;

  // Amounts
  customerRate?: number;
  customerAmount?: number;
  historicalRate?: number;
  finalSecondPrice?: number | null;
  finalProcCost?: number | null;
  procurementProvidedAmount?: number;
  profitAdjustedProcAmount?: number;   // adjusted proc rate (proc + 5%), shown to KAM
  confirmedAmount?: number;
  procHeadAmount?: number;

  // Spot - null price
  adjustedProcRate?: number;
  finalBdRate?: number | null;

  // Procurement / Proc Head shared
  procurementRate?: number | null;
  procurementPrice?: number | null; // best bid price
  targetCeiling?: number;
  remark?: string;

  // Contract
  contractRate?: number;

  // Points
  orderPoints?: any[];
  orderStatus?: any[];

  // Raw
  sourceId?: number;
  destinationId?: number;
  routeId?: number;
}

interface StatusMeta {
  label: string;
  badgeClass: string;
}

// ── Status → label/badge map (mirrors the React ORDER_STATUS map) ──
const ORDER_STATUS_MAP: Record<string, StatusMeta> = {
  CREATED: { label: 'Created', badgeClass: 'badge-yellow' },
  PRICING_PENDING: { label: 'Quote Pending on Procurement', badgeClass: 'badge-yellow' },
  QUOTE_PENDING: { label: 'Quote Pending on Procurement', badgeClass: 'badge-yellow' },
  DRAFT: { label: 'Draft', badgeClass: 'badge-grey' },
  IN_ACTIVE: { label: 'Inactive', badgeClass: 'badge-grey' },
  QUOTE_SHARED: { label: 'Quote Shared', badgeClass: 'badge-purple-outline' },
  HEAD_PENDING: { label: 'Pending on Head', badgeClass: 'badge-yellow' },
  HEAD_APPROVED: { label: 'Approved', badgeClass: 'badge-green' },
  HEAD_REJECTED: { label: 'Rejected', badgeClass: 'badge-red' },
  REJECTED: { label: 'Rejected', badgeClass: 'badge-red' },
  CANCELLED: { label: 'Cancelled', badgeClass: 'badge-red' },
  ORDER_CONFIRMED: { label: 'Confirmed', badgeClass: 'badge-green' },
};

interface Margin {
  pct: string;
  amt: number;
  isPositive: boolean;
}

@Component({
  selector: 'app-view-tms-order',
  templateUrl: './view-tms-order.component.html',
  styleUrls: ['./view-tms-order.component.css']
})
export class ViewTmsOrderComponent implements OnInit {
  theme = 'light';

  role: Role = 'ROLE_KAM';
  userName = '';
  order!: OrderViewModel;
  orderId: any;
  isLoading = false;
  actionLoading = false;

  // KAM — Final Bid Rate input (used when isNullPrice, and also post-Head-approval)
  finalBdRateInput: number | null = null;

  // HEAD — final approved amount + optional vendor
  procHeadAmountInput: number | null = null;
  vendorList: any[] = [];
  kamList: any[] = [];
  allRoles: any;
  procurementExecList: any[] = [];

  // ── Head reject (with remarks) dialog state ──
  showHeadRejectModal = false;
  headRejectRemark = '';
  rejectingHead = false;

  // ── Submit Rate dialog state ──
  showSubmitRateDialog = false;
  submitRateStep: 'confirm' | 'form' = 'confirm';
  inviteVendors = false;
  currentOrderForRate: any = null;
  rateForm!: FormGroup;

  showRejectModal = false;
  rejectRemark = '';
  rejectLoading = false;
  alreadyInvitedVendorIds: any[] = [];
  roleNames: string[] = [];

  assignVendorList: any[] = [];
  selectedVendorId: any;

  isLoadingVendors = false;
  vendorPage = 0;
  vendorPageSize = 20;
  vendorSearchTerm = '';
  vendorHasMore = true;
  private vendorSearch$ = new Subject<string>();
  loggedInUserId: any;
  isOrderCreator=false;
  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private orderService: ordersService,
    private messageService: MessageService,
    private fb: FormBuilder,
    private emsServices: EmsServicesService,
    private tabService: TabService
  ) {}

  ngOnInit(): void {
    const userDetails = JSON.parse(localStorage.getItem('UserDetails') || '{}');
    this.roleNames = (userDetails?.userRoles || userDetails?.userRoles || []).map((r: any) => r.name);

    // this.role = userDetails?.roleName || 'ROLE_KAM';
    this.userName = userDetails?.name || '';
    this.loggedInUserId = userDetails?.id;
    this.orderId = this.activeRoute.snapshot.params['id'];
    if (this.orderId) {
      this.getOrderDatabyId(this.orderId);
    }

    if (this.isProcHeadRole()) {
      this.vendorSearch$.pipe(
        debounceTime(400),
        distinctUntilChanged(),
        tap(() => {
          // reset pagination on new search
          this.vendorPage = 0;
          this.assignVendorList = [];
          this.vendorHasMore = true;
        }),
        switchMap(term => {
          this.vendorSearchTerm = term;
          this.isLoadingVendors = true;
          const filters = term ? [{ key: 'VENDOR_NAME_IN', value: term }] : [];
          return this.orderService.getVendorInfo(filters, this.vendorPage, this.vendorPageSize);
        })
      ).subscribe((res: any) => {
        this.assignVendorList = res?.responseObject?.content || [];
        this.vendorHasMore = !(res?.responseObject?.last ?? true); // adjust to your API's pagination fields
        this.isLoadingVendors = false;
      });
  
      // initial load
      this.getVendorList(true);
    }
    this.getRoles();
  }

  hasRole(...roles: string[]): boolean {
    return roles.some(r => this.roleNames.includes(r));
  }
  notRole(...roles: string[]): boolean {
    return !this.hasRole(...roles);
  }

  getRoles() {
    this.emsServices.getRoles().subscribe((res: any) => {
      this.allRoles = res?.responseObject;
      this.allRoles.forEach((el: any) => {
        if (el.name === 'ROLE_KAM') {
          this.getKam(el.id);
        }
        if (el.name === 'ROLE_PROCUREMENT') {
          this.getProcurement(el.id);
        }
      });
    });
  }

  getKam(id: any) {
    const postData = [{ key: 'ROLE_ID_IN', value: id.toString() }];
    this.emsServices.getUsers(postData, 0, 100000, 'id', 'DESC').subscribe((res: any) => {
      this.kamList = res?.responseObject?.content;
    });
  }

  getProcurement(id: any) {
    const postData = [{ key: 'ROLE_ID_IN', value: id.toString() }];
    this.emsServices.getUsers(postData, 0, 100000, 'id', 'DESC').subscribe((res: any) => {
      this.procurementExecList = res?.responseObject?.content;
    });
  }

  getVendorList(reset: boolean = false): void {
    if (reset) {
      this.vendorPage = 0;
      this.assignVendorList = [];
      this.vendorHasMore = true;
    }

    if (this.isLoadingVendors || !this.vendorHasMore) {
      return;
    }

    this.isLoadingVendors = true;
    const filters = this.vendorSearchTerm
      ? [{ key: 'VENDOR_NAME_IN', value: this.vendorSearchTerm }]
      : [];

    this.orderService.getVendorInfo(filters, this.vendorPage, this.vendorPageSize)
      .subscribe((res: any) => {
        const newItems = res?.responseObject?.content || [];
        this.assignVendorList = reset ? newItems : [...this.assignVendorList, ...newItems];
        this.vendorHasMore = !(res?.responseObject?.last ?? true);
        this.isLoadingVendors = false;
      }, () => {
        this.isLoadingVendors = false;
      });
  }

  onVendorSearch(event: any): void {
    this.vendorSearch$.next(event.term || '');
  }

  loadMoreVendors(): void {
    this.vendorPage++;
    this.getVendorList(false);
  }

  ngOnDestroy(): void {
    this.vendorSearch$.complete();
  }

  getVendorInfo(): void {
    this.orderService.getVendorInfo([], 0, 100000).subscribe((res: any) => {
      this.vendorList = res?.responseObject?.content || [];
    });
  }

  getOrderDatabyId(orderId: any) {
    this.isLoading = true;
    this.orderService.getOrderById(orderId).subscribe((res: any) => {
      this.isLoading = false;
      const data = res?.responseObject;
      this.isOrderCreator = this.loggedInUserId != null && data?.createdBy === this.loggedInUserId;
      if (data) {
        this.mapResponseToOrder(data);
      }
    }, () => {
      this.isLoading = false;
    });
  }

  mapResponseToOrder(data: any): void {
    const customerRateType: CustomerRateType = data.isNullPrice ? 'NULL_PRICE' : 'RATE_KNOWN';

    const pickup = data.orderPoints?.find((p: any) => p.pointType === 'PICKUP');
    const drop = data.orderPoints?.find((p: any) => p.pointType === 'DROP');
    const route = (data.source && data.destination)
      ? `${data.source} - ${data.destination}`
      : (pickup && drop)
        ? `${pickup.address} - ${drop.address}`
        : '';

    this.order = {
      id: data.id,
      orderNo: data.orderNumber,
      orderNumber: data.orderNumber,
      lrNumber: data.lrNumber || null,
      customer: data.customerName || '',
      customerName: data.customerName || '',
      accountId: data.accountId,
      customerId: data.customerId,
      vendorId: data.vendorId,
      orderType: data.orderType || 'spot',
      vehicleType: data.vehicleType || '',
      vehiclePd: data.vehicleRequiredAt || '',
      vehicleRequiredAt: data.vehicleRequiredAt || '',
      tripStatus: data.tripStatus || data.status || '',
      route,
      source: data.source || pickup?.address || '',
      destination: data.destination || drop?.address || '',
      assignedKam: data.assignedKam || data.assignedKamId || '',
      assignedKamId: data.assignedKamId,
      vendorName: data?.vendorName || '',
      assignedProcExec: data.assignedProcurement || data.assignedProcurementId || '',
      assignedProcurementId: data.assignedProcurementId,
      numberOfVehicle: data.numberOfVehicle || 1,
      vehicleTypeId: data.vehicleTypeId,
      status: data.status || '',
      biddingStatus: data.biddingStatus || '',
      biddingEnable: data.biddingEnable || 0,
      customerRateType,
      isNullPrice: data.isNullPrice,
      customerRate: data.customerAmount,
      customerAmount: data.customerAmount,
      historicalRate: data.historicalRate || null,
      finalSecondPrice: data.finalSecondPrice || null,
      finalProcCost: data.finalConfirmedAmount || null,
      procurementProvidedAmount: data.procurementProvidedAmount,
      profitAdjustedCustomerAmount: data.profitAdjustedCustomerAmount,
      profitAdjustedProcAmount: data.profitAdjustedProcAmount,
      confirmedAmount: data.confirmedAmount,
      procHeadAmount: data.procHeadAmount,
      procurementRate: data.procurementProvidedAmount || null,
      procurementPrice: data.procurementPrice || null,
      adjustedProcRate: data.profitAdjustedProcAmount ?? undefined,
      finalBdRate: data.customerAmount || null,
      targetCeiling: data.targetCeiling || undefined,
      remark: data.remarks || '',
      contractRate: data.contractRate || undefined,
      orderPoints: data.orderPoints || [],
      orderStatus: data.orderStatus || [],
      sourceId: data.sourceId,
      destinationId: data.destinationId,
      routeId: data.routeId,
    };
    console.log(this.order,'<-------------------------ORDERS')

    // Pre-fill inputs from existing data.
    // Once Head has approved, KAM only ever sees "Customer Rate" on screen
    // (order.procHeadAmount is never rendered to KAM — see template). So the
    // editable field must default to, and be floored against, customerAmount
    // — NOT procHeadAmount — otherwise the number KAM sees and the number
    // KAM is validated against can disagree (e.g. field defaults to a stale
    // confirmedAmount while the visible Customer Rate is higher).
    this.finalBdRateInput = data.status === 'HEAD_APPROVED'
      ? (data.customerAmount ?? data.procHeadAmount ?? null)
      : (data.confirmedAmount ?? data.customerAmount ?? null);
    this.procHeadAmountInput = data.procHeadAmount || null;
  }

  // ─────────────────────────────────────────────
  // STATUS + PILL LABEL (role-aware, mirrors React resolveDotPillLabel)
  // ─────────────────────────────────────────────

  private resolveStatus(value: string | undefined | null): StatusMeta {
    const key = (value || '').toUpperCase().replace(/-|\s/g, '_');
    if (ORDER_STATUS_MAP[key]) return ORDER_STATUS_MAP[key];
    if (!value) return { label: '--', badgeClass: 'badge-grey' };
    const label = value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c: string) => c.toUpperCase());
    return { label, badgeClass: 'badge-grey' };
  }

  private resolveDotPillLabel(): StatusMeta {
    const key = (this.order?.status || '').toUpperCase().replace(/-|\s/g, '_');
    if (key === 'QUOTE_SHARED') {
      if (this.hasRole('ROLE_KAM')) {
        return { label: 'Quote Received', badgeClass: 'badge-green' };
      }
      return { label: 'Quote Shared', badgeClass: 'badge-purple-outline' };
    }
    return this.resolveStatus(this.order?.status);
  }

  statusLabel(): string {
    if (!this.order) return '--';
    return this.resolveDotPillLabel().label;
  }

  statusBadgeClass(): string {
    if (!this.order) return 'badge-grey';
    return this.resolveDotPillLabel().badgeClass;
  }

  // ─────────────────────────────────────────────
  // ROLE HELPERS
  // ─────────────────────────────────────────────

  isKAM(): boolean {
    return this.hasRole('ROLE_KAM');
  }
  
  isSuperuser(): boolean {
    return this.hasRole('ROLE_SUPERUSER');
  }
  
  isProcHeadRole(): boolean {
    return this.hasRole('ROLE_PROCUREMENT_HEAD');
  }
  
  isProcurementAny(): boolean {
    return this.hasRole('ROLE_PROCUREMENT') || this.isProcHeadRole();
  }
  isProcurementOnly(): boolean {
    return this.hasRole('ROLE_PROCUREMENT');
  }
  // ─────────────────────────────────────────────
  // SECTION VISIBILITY / DERIVED VALUES
  // ─────────────────────────────────────────────

  showPricingSection(): boolean {
    return this.notRole('ROLE_VENDOR') && this.notRole('ROLE_OPERATION');
  }

  pricingSectionTitle(): string {
    if (this.isKAM()) return 'Pricing';
    if (this.hasRole('ROLE_PROCUREMENT')) return 'Pricing — Procurement';
    if (this.isProcHeadRole()) return 'Pricing — Full View';
    return 'Pricing';
  }

  isSpot(): boolean {
    return this.order?.orderType?.toLowerCase() === 'spot';
  }

  isContract(): boolean {
    return this.order?.orderType?.toLowerCase() === 'contract';
  }

  isNotPrice(): boolean {
    return !!this.order?.isNullPrice;
  }

  hasProcQuote(): boolean {
    const v = this.order?.procurementProvidedAmount;
    return v != null && v !== 0;
  }

  hasCustomerRate(): boolean {
    const v = this.order?.customerAmount;
    return v != null && (v as any) !== 0;
  }

  hasHeadRate(): boolean {
    const v = this.order?.procHeadAmount;
    return v != null && (v as any) !== 0;
  }

  showNullPriceBanner(): boolean {
    // Mirrors React's KAM-specific "no customer rate set" banner
    return this.isKAM() && !this.hasCustomerRate();
  }

  showKAMActions(): boolean {
    return this.isKAM() && ['QUOTE_SHARED', 'HEAD_APPROVED'].includes(this.order?.status);
  }

  // Whether the "enter/confirm your final rate" input should render for KAM.
  // Shows for both pre-Head confirmation (QUOTE_SHARED) and post-Head
  // confirmation (HEAD_APPROVED) — in the QUOTE_SHARED case, going below the
  // procurement threshold routes to Head; in the HEAD_APPROVED case, the
  // entered amount is floored at the Head-approved amount instead
  // (see belowHeadFloor()).
  showFinalBidRatePanel(): boolean {
    return this.showKAMActions();
  }

  // True once Head has already approved the order and KAM is confirming
  // against that approved amount (as opposed to the original QUOTE_SHARED
  // confirmation flow, which can still route to Head).
  isPostHeadApproval(): boolean {
    return this.order?.status === 'HEAD_APPROVED';
  }

  showHeadPanel(): boolean {
    return this.isProcHeadRole() && this.order?.status === 'HEAD_PENDING';
  }

  showApprovalRequiredBanner(): boolean {
    return this.isProcHeadRole() && this.order?.status === 'HEAD_PENDING';
  }

  biddingCompleted(): boolean {
    return (this.order?.biddingStatus || '').toLowerCase() === 'completed';
  }

  // FIX: previously this returned the "Enter Final Bid Rate" hint for KAM
  // as soon as the order was null-price — even while it was still sitting
  // at CREATED/QUOTE_PENDING and procurement hadn't quoted yet. Now it only
  // shows once there's actually something for the KAM to act on.
  infoBannerText(): string | null {
    if (!this.order) return null;
    if (!this.order.isNullPrice) {
      if (this.isKAM() || this.hasRole('ROLE_PROCUREMENT')) {
        return 'Customer rate is the final billing amount.';
      }
    } else {
      if (this.isKAM()) {
        if (this.hasProcQuote() && this.order.status !== 'ORDER_CONFIRMED') {
          return 'Enter Final Bid Rate. If less than the procurement rate, the order goes to Head for approval.';
        }
        return null;
      }
      if (this.hasRole('ROLE_PROCUREMENT')) {
        return 'No customer rate. Quote freely.';
      }
    }
    return null;
  }

  // Dynamic label so KAM can see up front whether hitting the button will
  // confirm the order outright or send it to Head for approval.
  confirmButtonLabel(): string {
    if (this.actionLoading) return 'Confirming...';
    // Once Head has approved, confirming always finalizes the order — there's
    // no further routing possible, so never show "Send for Head Approval" here.
    if (this.isPostHeadApproval()) return '✓ Confirm Order';
    if (this.showFinalBidRatePanel() && this.finalBdRateInput && this.order?.profitAdjustedProcAmount) {
      const entered = parseFloat(this.finalBdRateInput as any) || 0;
      const threshold = parseFloat(this.order.profitAdjustedProcAmount as any) || 0;
      if (entered < threshold) return 'Send for Head Approval';
    }
    return '✓ Confirm Order';
  }

  // Whether a hard floor applies to KAM's entered rate for this order.
  //  1. Post-Head-approval (any order type) — cannot go below the approved
  //     Customer Rate.
  //  2. Known-price Spot orders (isNullPrice === false) — cannot lower the
  //     rate below the existing Customer Rate even pre-Head-approval, since
  //     there's no procurement-threshold routing to fall back on for these.
  hasRateFloor(): boolean {
    if (this.isPostHeadApproval()) return true;
    return this.isSpot() && !this.isNotPrice();
  }

  // The floor KAM cannot go below. This must be the *visible* Customer Rate
  // (order.customerAmount) — the amount actually shown on screen — not
  // order.procHeadAmount, which KAM never sees. Falls back to procHeadAmount
  // only if customerAmount is missing.
  private rateFloor(): number {
    return parseFloat(this.order?.customerAmount as any)
      || parseFloat(this.order?.procHeadAmount as any)
      || 0;
  }

  // True when KAM's entered amount has been typed below the applicable
  // floor (see hasRateFloor()). Used to show a warning and disable the
  // confirm button.
  belowHeadFloor(): boolean {
    if (!this.hasRateFloor()) return false;
    const entered = parseFloat(this.finalBdRateInput as any) || 0;
    const floor = this.rateFloor();
    return !!floor && entered < floor;
  }

  // ─────────────────────────────────────────────
  // MARGIN HELPERS (mirrors React calcMargin)
  // ─────────────────────────────────────────────

  private calcMargin(customerRate: any, compareAmount: any): Margin | null {
    const cust = Number(customerRate);
    const comp = Number(compareAmount);
  
    if (isNaN(cust) || isNaN(comp) || cust <= 0 || comp <= 0) {
      return null;
    }
  
    const diff = cust - comp;
  
    return {
      amt: diff,
      pct: ((diff / comp) * 100).toFixed(1), // <-- use compareAmount
      isPositive: diff >= 0
    };
  }

  pricingCardMargin(): Margin | null {
    if (!this.isProcHeadRole() || !this.order) return null;
    return this.calcMargin(this.order.customerAmount, this.order.procurementProvidedAmount);
  }

  headLiveMargin(): Margin | null {
    if (!this.order) return null;
    return this.calcMargin(this.order.customerAmount, this.procHeadAmountInput);
  }

  absVal(v: string | number): string {
    return Math.round(Math.abs(parseFloat(String(v)))).toString();
  }

  // ─────────────────────────────────────────────
  // EDITABILITY (General Details)
  // ─────────────────────────────────────────────

  canEditLrNumber(): boolean {
    return this.isKAM();
  }

  canEditProcExec(): boolean {
    return this.isKAM() || this.hasRole('ROLE_PROCUREMENT');
  }

  canEditGeneralDetails(): boolean {
    return this.isKAM() || this.isSuperuser();
  }

  // ─────────────────────────────────────────────
  // KAM — CONFIRM (unified: covers both "enter final bid rate" and
  // "confirm after Head approval", mirroring React's handleAction)
  // ─────────────────────────────────────────────

  confirmKAMOrder(): void {
    const needsRate = this.showFinalBidRatePanel();
    if (needsRate && !this.finalBdRateInput) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please enter the Final Rate' });
      return;
    }

    const postHead = this.isPostHeadApproval();

    // Hard floor: post-Head-approval (any order type) or known-price Spot
    // orders — KAM cannot confirm below the existing Customer Rate.
    if (this.hasRateFloor() && this.belowHeadFloor()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: `Amount cannot be less than the Customer Rate (${this.formatCurrency(this.rateFloor())})`
      });
      return;
    }

    let resolvedStatus = 'ORDER_CONFIRMED';
    if (needsRate && !postHead) {
      const entered = parseFloat(this.finalBdRateInput as any) || 0;
      const threshold = parseFloat(this.order.profitAdjustedProcAmount as any) || 0;
      // Only route to Head if there's an actual procurement threshold to
      // compare against and the entered amount falls below it. If the
      // backend hasn't sent a threshold for this order, don't block on it.
      // Once Head has already approved (postHead === true), there's nothing
      // left to route — confirming always finalizes the order.
      if (threshold && entered < threshold) {
        resolvedStatus = 'HEAD_PENDING';
      }
    }

    // FIX: previously gated on isNotPrice(), so an edited/confirmed rate on
    // a known-price order never made it into the payload — the backend kept
    // seeing the old customerAmount. Now driven by needsRate, so KAM's entry
    // (same, higher, or lower than before, and whether pre- or post-Head
    // approval) is always what gets sent as the confirmed amount.
    const confirmedAmount = (needsRate && this.finalBdRateInput)
      ? (parseFloat(this.finalBdRateInput as any) || 0)
      : (parseFloat(this.order.customerAmount as any) || 0);

    const payload: any = {
      id: this.order.id,
      status: resolvedStatus,
      confirmedAmount
    };

    if (needsRate && this.finalBdRateInput) {
      payload.customerAmount = parseFloat(this.finalBdRateInput as any) || 0;
    }

    this.actionLoading = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: () => {
        this.actionLoading = false;
        const msg = resolvedStatus === 'ORDER_CONFIRMED'
          ? 'Order confirmed successfully'
          : 'Order sent to Procurement Head for approval';
        this.messageService.add({ severity: 'success', summary: 'Success', detail: msg });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // KAM / generic — REJECT (plain REJECTED, no remarks)
  // ─────────────────────────────────────────────

  rejectOrder(): void {
    this.rejectRemark = '';
    this.showRejectModal = true;
  }
  
  closeRejectModal(): void {
    this.showRejectModal = false;
  }
  
  confirmReject(): void {
    if (!this.rejectRemark.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please enter rejection remarks'
      });
      return;
    }
  
    const payload = {
      id: this.order.id,
      status: 'REJECTED',
      remarks: this.rejectRemark.trim()
    };
  
    this.rejectLoading = true;
  
    this.orderService.createUpdateOrder(payload).subscribe({
      next: () => {
        this.rejectLoading = false;
        this.showRejectModal = false;
  
        this.messageService.add({
          severity: 'success',
          summary: 'Rejected',
          detail: 'Order rejected successfully'
        });
  
        this.getOrderDatabyId(this.orderId);
      },
      error: (err) => {
        this.rejectLoading = false;
  
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.responseMessage || 'Something went wrong'
        });
      }
    });
  }

  // ─────────────────────────────────────────────
  // PROC HEAD — APPROVE (vendor optional, mirrors React)
  // ─────────────────────────────────────────────

  submitHeadApproval(): void {
    if (!this.procHeadAmountInput || parseFloat(this.procHeadAmountInput as any) <= 0) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please enter a valid Final Amount' });
      return;
    }

    if (!this.selectedVendorId) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please assign a vendor' });
      return;
    }

    const payload: any = {
      id: this.order.id,
      status: 'HEAD_APPROVED',
      procHeadAmount: parseFloat(this.procHeadAmountInput as any) || 0,
      vendorId: this.selectedVendorId ? this.selectedVendorId : null,
      procurementProvidedAmount: parseFloat(this.procHeadAmountInput as any) || 0,
    };

    this.actionLoading = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: () => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Order approved' });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.actionLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // PROC HEAD — REJECT WITH REMARKS (separate status: HEAD_REJECTED)
  // ─────────────────────────────────────────────

  openHeadRejectModal(): void {
    this.headRejectRemark = '';
    this.showHeadRejectModal = true;
  }

  closeHeadRejectModal(): void {
    this.showHeadRejectModal = false;
  }

  confirmHeadReject(): void {
    if (!this.headRejectRemark?.trim()) {
      this.messageService.add({ severity: 'warn', summary: 'Warning', detail: 'Please enter a reason for rejection' });
      return;
    }

    const payload: any = {
      id: this.order.id,
      status: 'HEAD_REJECTED',
      remarks: this.headRejectRemark.trim()
    };

    this.rejectingHead = true;
    this.orderService.createUpdateOrder(payload).subscribe({
      next: () => {
        this.rejectingHead = false;
        this.showHeadRejectModal = false;
        this.messageService.add({ severity: 'success', summary: 'Rejected', detail: 'Order rejected' });
        this.getOrderDatabyId(this.orderId);
      },
      error: (err: any) => {
        this.rejectingHead = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.responseMessage || 'Something went wrong' });
      }
    });
  }

  // ─────────────────────────────────────────────
  // FORMATTING & NAVIGATION
  // ─────────────────────────────────────────────

  formatCurrency(value: number | null | undefined): string {
    if (value === null || value === undefined || (value as any) === '') return '';
    return '₹' + Number(value).toLocaleString('en-IN');
  }

  goBack(): void {
    this.router.navigate(['/orders']);
  }

  saveGeneralDetails(): void {
    console.log('Save general details');
  }

  loadExistingVendorInvites(orderId: any): void {
    const filters = [{ key: 'ORDER_ID', value: orderId.toString() }];
    this.orderService.getVendorOrders(filters, 0, 10000, 'id', 'DESC').subscribe({
      next: (res: any) => {
        const bids = res?.responseObject?.content || [];
        this.alreadyInvitedVendorIds = Array.from(
          new Set(bids.map((b: any) => b.vendorId).filter((v: any) => v != null))
        );
      },
      error: () => {
        this.alreadyInvitedVendorIds = [];
      }
    });
  }

  // ─────────────────────────────────────────────
  // PROCUREMENT — SUBMIT RATE DIALOG
  // ─────────────────────────────────────────────

  openSubmitRate(item: any): void {
    this.buildRateForm();
    this.getVendorInfo();
    this.loadExistingVendorInvites(item.id);
    this.currentOrderForRate = item;
    this.submitRateStep = 'confirm';
    this.inviteVendors = false;
    this.rateForm.reset();
    const ceiling = this.order?.profitAdjustedCustomerAmount;
    if (ceiling !== undefined && ceiling !== null) {
      this.rateForm.controls['rate'].setValue(ceiling.toString());
    }
    this.showSubmitRateDialog = true;
  }

  buildRateForm(): void {
    this.rateForm = this.fb.group({
      rate: [null, [Validators.required, Validators.min(1)]],
      vendors: [null, Validators.required]
    });
  }

  closeSubmitRateDialog(): void {
    this.showSubmitRateDialog = false;
  }

  onYesInvite(): void {
    this.inviteVendors = true;
    this.submitRateStep = 'form';
    this.rateForm.get('vendors')?.setValue([...this.alreadyInvitedVendorIds]);
  }

  onNoJustSubmit(): void {
    this.inviteVendors = false;
    this.submitRateStep = 'form';
  }

  onRateBack(): void {
    this.submitRateStep = 'confirm';
    this.rateForm.reset();
  }

  isRateInvalid(field: string): boolean {
    const ctrl = this.rateForm.get(field);
    return !!ctrl && ctrl.touched && ctrl.invalid;
  }

  toggleSelectAll(): void {
    const allIds = this.vendorList.map(v => v.id);
    const selected = this.rateForm.get('vendors')?.value || [];

    if (selected.length === this.vendorList.length) {
      this.rateForm.get('vendors')?.setValue([]);
    } else {
      this.rateForm.get('vendors')?.setValue(allIds);
    }
  }

  isAllSelected(): boolean {
    const selected = this.rateForm.get('vendors')?.value || [];
    return selected.length === this.vendorList.length && this.vendorList.length > 0;
  }

  isSomeSelected(): boolean {
    const selected = this.rateForm.get('vendors')?.value || [];
    return selected.length > 0 && selected.length < this.vendorList.length;
  }

  // Submit rate button on the bottom bar: visible for Procurement whenever
  // the order isn't already in a terminal state (mirrors React exactly).
  canShowSubmitRateButton(): boolean {
    if (this.notRole('ROLE_PROCUREMENT')) return false;
    const terminal = ['REJECTED', 'ORDER_CONFIRMED', 'HEAD_REJECTED', 'CANCELLED'];
    return !terminal.includes((this.order?.status || '').toUpperCase());
  }

  onSubmitRate(): void {
    if (this.rateForm.invalid) {
      this.rateForm.markAllAsTouched();
      return;
    }

    const formValue = this.rateForm.value;
    let payload: any;

    if (this.inviteVendors) {
      const newVendorIds = (formValue.vendors || [])
      .filter((id: any) => !this.alreadyInvitedVendorIds.includes(id));

      if (!newVendorIds.length) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Please select at least one new vendor to invite'
        });
        return;
      }
      payload = {
        id: this.currentOrderForRate?.id,
        procurementProvidedAmount: formValue.rate,
        biddingEnable: 1,
        biddingStatus: 'PendingOnVendor',
        vendorIds: newVendorIds
      };
    } else {
      const currentStatus = (this.order?.status || '').toUpperCase();
      const newStatus = (currentStatus === 'CREATED' || currentStatus === 'QUOTE_PENDING')
        ? 'QUOTE_SHARED'
        : currentStatus;

      payload = {
        id: this.currentOrderForRate?.id,
        procurementProvidedAmount: formValue.rate,
        status: newStatus,
        vendorId: formValue.vendors
      };
    }

    this.orderService.createUpdateOrder(payload).subscribe({
      next: (res: any) => {
        this.showSubmitRateDialog = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: res?.responseMessage || 'Rate submitted successfully'
        });
        this.getOrderDatabyId(this.orderId);
        this.goBack()
        
      },
      error: (err: any) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.responseMessage || 'Something went wrong'
        });
      }
    });
  }

  checkTripStatus(item: any,status:any): boolean {
    return item?.orderStatus?.some(
      (el: any) =>
        el.statusType === 'TRIP_STATUS' &&
        el.status === status
    ) || false;
  }

  startTrip(item: any) {
    const isPending = this.checkTripStatus(item, 'PENDING');
  
    if (isPending) {
      const payload = {
        orderId: item?.id,
        accountId: item?.accountId,
        customerId: item?.customerId,
        totalAmount: item?.confirmedAmount,
        status: 'TRIP_GENERATED',
        vendorId: item?.vendorId,
      };
  
      this.orderService.tripGenration(payload)
        .subscribe({
          next: () => this.goToTripPage(item),
          error: (err: any) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: err?.error?.responseMessage || 'Failed to create trip'
            });
          }
        });
    } else {
      this.goToTripPage(item);
    }
  }
  
  private goToTripPage(item: any) {
    this.tabService.addTab({
      label: 'Order Trip',
      path: 'orders/order-trip/' + item?.id,
      fullPath: '',
      filterData: [],
    });
    this.router.navigate(['orders/order-trip/' + item?.id]);
  }
}